import {
    ChangeDetectionStrategy,
    Component,
    inject,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    FormArray,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, ParamMap, RouterModule } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridOptions,
    GridReadyEvent,
    GridSizeChangedEvent,
    ValueFormatterParams,
    ValueGetterParams,
} from 'ag-grid-community';
import { IMask } from 'angular-imask';
import {
    EMPTY,
    map,
    merge,
    Observable,
    of,
    Subject,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';
import { AccessConstants } from '../../../../core/constants/access-constants';
import { ExternalLinks } from '../../../../core/constants/external-links';
import { IMaskConstants } from '../../../../core/constants/imask';
import { CardTypeEnum } from '../../../../core/enums/commerce/cards-on-file/card-types';
import { OrderPaymentTransactionStatus } from '../../../../core/enums/commerce/payment/order-payment-transaction-status';
import {
    InvoiceNet30TransitionablePaymentMethods,
    PaymentMethod,
} from '../../../../core/enums/commerce/payment/payment-method';
import { UserRole } from '../../../../core/enums/users/role';
import { parseDateToEasternGridFormat } from '../../../../core/functions/date-helpers';
import { hasOpenDialogs } from '../../../../core/functions/dialog-helper';
import { parseMaskedStringToNumber } from '../../../../core/functions/number-helpers';
import { openPdfInNewWindow } from '../../../../core/functions/pdf-window-helper';
import { nameOf, titleize } from '../../../../core/functions/string-helpers';
import { CreditCardSummary } from '../../../../core/models/commerce/cards-on-file/credit-card-summary';
import { GetEligibleCardsForOrderResponse } from '../../../../core/models/commerce/cards-on-file/get-eligible-cards-for-order-response';
import { OrderCreditCardSummary } from '../../../../core/models/commerce/cards-on-file/order-credit-card-summary';
import { DropdownOption } from '../../../../core/models/dropdown-option';
import {
    FormGroupType,
    NestedFormGroupType,
} from '../../../../core/models/form-group-type';
import { FormInputCellRendererParams } from '../../../../core/models/grid/form-input-cell-renderer-params';
import { GridHelper } from '../../../../core/models/grid/grid-helper';
import { RegExPattern } from '../../../../core/models/regexp-pattern/regex-pattern';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { CreditCardDialogService } from '../../../../core/services/commerce/credit-card-dialog/credit-card-dialog.service';
import { CreditCardSummaryService } from '../../../../core/services/commerce/credit-card-summary/credit-card-summary.service';
import { CreditCardService } from '../../../../core/services/commerce/credit-card/credit-card.service';
import { CurrencyFormattingService } from '../../../../core/services/currency-formatting/currency-formatting.service';
import { FormEntryService } from '../../../../core/services/form-entry/form-entry.service';
import { PostalCodeService } from '../../../../core/services/postal-code/postal-code.service';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { ResourcesService } from '../../../../core/services/resources/resources.service';
import { CardTypeIconComponent } from '../../../../shared/components/card-type-icon/card-type-icon.component';
import { GridToolbarComponent } from '../../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { GridToolbarOptions } from '../../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { HoverTooltipComponent } from '../../../../shared/components/tooltips/hover-tooltip/hover-tooltip.component';
import { RegexInputDirective } from '../../../../shared/directives/regex-input/regex-input.directive';
import { UserStateService } from '../../../user/shared/services/user-state/user-state.service';
import { OrderSummaryHeaderComponent } from '../shared/components/order-summary-header/order-summary-header.component';
import { CardOnFileValidationMessages } from '../shared/constants/card-on-file-validation-messages';
import { OrderConstants } from '../shared/constants/order-constants';
import { OrderPaymentConstants } from '../shared/constants/order-payment-constants';
import { OrderSaveConfirmationDialogMessages } from '../shared/constants/order-save-confirmation-dialog-messages';
import {
    OrderStatus,
    OrderStatusLabels,
} from '../shared/constants/order-status';
import { OrderDetailsActionService } from '../shared/services/order-details-action/order-details-action.service';
import { OrderFormService } from '../shared/services/order-form/order-form.service';
import { OrderService } from '../shared/services/order/order.service';
import { OrderBillingInformationFormModalComponent } from './components/order-billing-information-form-modal/order-billing-information-form-modal.component';
import { OrderLineItemFormModalComponent } from './components/order-line-item-form-modal/order-line-item-form-modal.component';
import { OrderDetailsConstants } from './constants/order-details-constants';
import { OrderLineItemConstants } from './constants/order-line-item-constants';
import { OrderLineItemMaskConfig } from './constants/order-line-items-mask-config';
import { OrderPrintBillingDocumentWarningDialogMessage } from './constants/order-print-billing-document-warning-dialog-message';
import { PaymentMethodRequiredMessages } from './constants/payment-method-required-messages';
import { PreferredPaymentMethodFormMessages } from './constants/preferred-payment-method-form-messages';
import { OrderDetailsData } from './models/order-details-data';
import { OrderDetailsFormModel } from './models/order-details-form-model';
import { OrderDetailsGridApiMap } from './models/order-details-grid-api-map';
import { OrderDetailsGridType } from './models/order-details-grid-type';
import { OrderDetailsPaymentTransactionResponse } from './models/order-details-payment-transaction-response';
import { OrderDetailsResponse } from './models/order-details-response';
import { OrderDetailsRowDataMap } from './models/order-details-row-data-map';
import { OrderFinancialSummaryRowData } from './models/order-financial-summary-row-data';
import { OrderHistoryLogRowData } from './models/order-history-log-row-data';
import { OrderLineItemFormModel } from './models/order-line-item-form-model';
import { PreferredPaymentMethodFormModel } from './models/preferred-payment-method-form-model';
import { SaveOrderRequest } from './models/save-order-request';
import { SetPreferredPaymentMethodRequest } from './models/set-preferred-payment-method-request';
import { OrderDetailsFormService } from './services/order-details-form.service';
import { OrderLineItemFormService } from './services/order-line-item-form.service';
import { PreferredPaymentMethodFormService } from './services/preferred-payment-method-form.service';

@Component({
    selector: 'app-order-details',
    templateUrl: './order-details.component.html',
    styleUrl: './order-details.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        MatRadioModule,
        MatExpansionModule,
        MatIconModule,
        FormsModule,
        RegexInputDirective,
        MatInputModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        RouterModule,
        AgGridAngular,
        GridToolbarComponent,
        MatSelectModule,
        MatButtonModule,
        OrderSummaryHeaderComponent,
        HoverTooltipComponent,
        CardTypeIconComponent,
    ],
})
export class OrderDetailsComponent implements OnInit, OnDestroy {
    private readonly destroy$ = new Subject<void>();

    readonly externalLinks = ExternalLinks;

    /**
     * Subject that emits after the order details have been successfully saved
     * and the latest data has been reloaded, allowing subscribers to react to
     * save completion (e.g., UI updates or navigation).
     */
    private readonly saveOrderDetailsConfirmation$ = new Subject<void>();

    /**
     * Subject that emits after the preferred payment method has been successfully saved
     * and the latest data has been reloaded, allowing subscribers to react to
     * save completion (e.g., UI updates or navigation).
     */
    private readonly savePreferredPaymentMethodConfirmation$ =
        new Subject<void>();

    private readonly dialog = inject(MatDialog);
    private readonly route = inject(ActivatedRoute);
    private readonly orderService = inject(OrderService);
    private readonly alertService = inject(AlertService);
    private readonly dropdownService = inject(DropdownService);
    private readonly formEntryService = inject(FormEntryService);
    private readonly userStateService = inject(UserStateService);
    private readonly resourcesService = inject(ResourcesService);
    private readonly orderFormService = inject(OrderFormService);
    private readonly creditCardService = inject(CreditCardService);
    private readonly postalCodeService = inject(PostalCodeService);
    private readonly creditCardDialogService = inject(CreditCardDialogService);
    private readonly orderDetailsFormService = inject(OrderDetailsFormService);
    private readonly creditCardSummaryService = inject(
        CreditCardSummaryService,
    );
    private readonly orderLineItemFormService = inject(
        OrderLineItemFormService,
    );
    private readonly currencyFormattingService = inject(
        CurrencyFormattingService,
    );
    private readonly orderDetailsActionsService = inject(
        OrderDetailsActionService,
    );
    private readonly preferredPaymentMethodFormService = inject(
        PreferredPaymentMethodFormService,
    );

    /**
     * Indicates whether there are unsaved changes in the form.
     * This flag is used to prompt the user when they attempt
     * to navigate away from the page with unsaved changes.
     */
    hasChanges: boolean = false;

    /**
     * Indicates whether the preferred payment method requirement is currently satisfied.
     *
     * This is true when a selection is not required (order is not pending), or when a
     * selection is required and a preferred payment method has been selected.
     *
     * This flag is used to determine whether to show a dialog when the user attempts
     * to navigate away without satisfying the requirement.
     */
    isPreferredPaymentMethodRequirementSatisfied: boolean = false;

    /**
     * The ID of the order being displayed, extracted from the route parameters.
     */
    orderId: string | null = null;

    /**
     * Holds the order details data to be displayed on the page.
     */
    orderDetails: OrderDetailsData = {} as OrderDetailsData;

    /**
     * The user associated with the order's saved credit cards, used to populate the cards on file
     * dropdown and pre-select the default card if applicable.
     */
    cardsOnFile: OrderCreditCardSummary[] = [];

    /**
     * The form group for the main order details form, including status, notes, and order line items.
     */
    orderDetailsForm: FormGroup<NestedFormGroupType<OrderDetailsFormModel>> =
        this.orderDetailsFormService.getFormDefinition();

    /**
     * The permitted status transitions for orders.
     * This is used to determine which status options should be
     * enabled or disabled in the order status dropdown based on
     * the current status of the order.
     */
    permittedOrderStatusTransitions: Partial<
        Record<keyof typeof OrderStatus, OrderStatus[]>
    > = {};

    /**
     * Dropdown options for the order status field in the form.
     */
    orderStatusDropdownOptions: DropdownOption[] = [];

    /**
     * The form group for the preferred payment method section of the order details page.
     */
    preferredPaymentMethodForm: FormGroup<
        FormGroupType<PreferredPaymentMethodFormModel>
    > = this.preferredPaymentMethodFormService.getFormDefinition();

    /**
     * Dropdown options for the cards on file field in the preferred payment method form.
     */
    cardsOnFileDropdownOptions: DropdownOption[] = [];

    /**
     * AG Grid API instances for the order details grids.
     */
    gridApis: OrderDetailsGridApiMap = {} as OrderDetailsGridApiMap;

    /**
     * Row data for the order details grids.
     */
    rowData: OrderDetailsRowDataMap = {
        [OrderDetailsGridType.orderLineItemsEditable]: [],
        [OrderDetailsGridType.orderLineItemsReadonly]: [],
        [OrderDetailsGridType.orderFinancialSummary]: [],
        [OrderDetailsGridType.orderHistoryLogs]: [],
    };

    readonly maskOptions = IMaskConstants;
    readonly regExPattern = RegExPattern;
    readonly paymentMethod = PaymentMethod;
    readonly cardTypeEnum = CardTypeEnum;
    readonly orderConstants = OrderConstants;
    readonly orderDetailsConstants = OrderDetailsConstants;
    readonly orderPaymentConstants = OrderPaymentConstants;
    readonly orderDetailsGridType = OrderDetailsGridType;

    /**
     * Grid options for the order line items grid for the elevated role view
     */
    orderLineItemsEditableGridOptions: GridOptions = {
        ...GridHelper.defaultGridOptionsNoPaginationUnstyledCells,
        ...GridHelper.defaultGridOptionsRowDrag,
        domLayout: 'autoHeight',
        columnDefs: this.orderLineItemsEditableGridColumnDefs(),
        getRowId: GridHelper.defaultGetRowId,
        onRowDragEnd: this.updateOrderLineItemWeights.bind(this),
    };

    /**
     * Grid options for the order line items grid for the non-elevated role view
     */
    orderLineItemsReadonlyGridOptions: GridOptions = {
        ...GridHelper.defaultGridOptionsNoPaginationUnstyledCells,
        domLayout: 'autoHeight',
        columnDefs: this.orderLineItemsReadonlyGridColumnDefs(),
    };

    /**
     * Shared grid toolbar options for the order line items grid, used in both elevated and non-elevated role views
     */
    orderLineItemGridToolbarOptions: GridToolbarOptions = {
        quickFilter: false,
        exportCsv: false,
        refreshDataForExport: () => {},
    };

    /**
     * Grid options for the order financial summary grid
     */
    orderFinancialSummaryGridOptions: GridOptions = {
        ...GridHelper.defaultGridOptionsNoPagination,
        domLayout: 'autoHeight',
        columnDefs: this.orderFinancialSummaryGridColumnDefs(),
        suppressHorizontalScroll: true,
        onGridSizeChanged: (params: GridSizeChangedEvent) => {
            params.api.sizeColumnsToFit();
        },
        defaultColDef: {
            cellClass: 'bg-neutral-400',
        },
    };

    /**
     * Grid options for the order history logs grid
     */
    orderHistoryLogsGridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
        domLayout: 'autoHeight',
        columnDefs: this.orderHistoryLogsGridColumnDefs(),
    };

    get status(): FormControl {
        return this.orderDetailsForm.controls.status as FormControl;
    }

    get poNumber(): FormControl {
        return this.orderDetailsForm.controls.poNumber as FormControl;
    }

    get notes(): FormControl {
        return this.orderDetailsForm.controls.notes as FormControl;
    }

    /**
     * A computed property that formats the city, state, and zip code into a single string for display purposes.
     *
     * @returns A string in the format "City, State, ZipCode" with appropriate formatting applied to the zip code.
     */
    get cityStateZip(): string {
        const { city, state, zipCode } = this.orderDetails;
        const postalCodeFormatted =
            this.postalCodeService.buildPostalCodeString(zipCode ?? '');

        return [city, state, postalCodeFormatted].filter(Boolean).join(', ');
    }

    get orderLineItems(): FormArray<
        FormGroup<FormGroupType<OrderLineItemFormModel>>
    > {
        return this.orderDetailsForm.controls.orderLineItems as FormArray;
    }

    get selectedCardOnFile(): CreditCardSummary | null {
        return (
            this.cardsOnFile.find(
                (card) =>
                    card.id ===
                    this.preferredPaymentMethodForm.controls.cardOnFileId.value,
            ) ?? null
        );
    }

    ngOnInit(): void {
        this.loadOrderDetails().subscribe(() => {
            this.setupOrderDetailsDropdownOptions();
            this.setupOrderDetailsFormListeners();
            this.setupPreferredPaymentMethodFormListeners();
            this.handleOrderDetailsFormDirtying();
            this.handleOrderDetailsSaveChangesClick();
            this.handleOrderDetailsPrintClick();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();

        // These subjects are used to trigger unsubscription of form listeners that are set up in this component
        // to prevent memory leaks and unintended side effects from lingering subscriptions after the component is destroyed.
        this.saveOrderDetailsConfirmation$.next();
        this.saveOrderDetailsConfirmation$.complete();
        this.savePreferredPaymentMethodConfirmation$.next();
        this.savePreferredPaymentMethodConfirmation$.complete();
    }

    onGridReady(params: GridReadyEvent, type: OrderDetailsGridType) {
        this.gridApis[type] = params.api;
        this.initializeGridData(type);
        this.setupGridAlignment();
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    initializeGridContext(gridType: OrderDetailsGridType): any {
        switch (gridType) {
            case OrderDetailsGridType.orderLineItemsEditable:
                return this.getOrderLineItemsEditableGridContext();
            case OrderDetailsGridType.orderLineItemsReadonly:
                return this.getOrderLineItemsReadonlyGridContext();
            case OrderDetailsGridType.orderFinancialSummary:
                return this.getOrderFinancialSummaryGridContext();
            case OrderDetailsGridType.orderHistoryLogs:
                return this.getOrderHistoryLogsGridContext();
            default: {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _exhaustive: never = gridType;
                throw new Error(`Unhandled grid type: ${gridType}`);
            }
        }
    }

    /**
     * Modifications are only permitted when the order is in a pending status. Users, regardless of role,
     * are only able to view the order when it is in any other status.
     *
     * @returns True if the order is in a pending status, false otherwise.
     */
    isReadOnly(): boolean {
        const statusControl = this.status;

        if (!statusControl) {
            // If the status control is not initialized, default to read-only.
            return true;
        }

        return statusControl.value !== OrderStatus.Pending;
    }

    hasElevatedRole(): boolean {
        const authenticatedUser = this.userStateService.getUser();
        if (!authenticatedUser) {
            throw new Error('User is not authenticated');
        }

        return AccessConstants.RolesThatCanAccessAllOrders.includes(
            authenticatedUser.role,
        );
    }

    /**
     * Handles the click event for editing billing information.
     * It opens the form dialog and reloads the order details data upon successful update.
     */
    handleEditBillingInformationClick(): void {
        if (hasOpenDialogs(this.dialog)) {
            return;
        }

        this.dialog
            .open(OrderBillingInformationFormModalComponent, {
                width: '700px',
                disableClose: true,
                data: {
                    orderBillingInformationId:
                        this.orderDetails.orderBillingInformationId.toString(),
                },
            })
            .afterClosed()
            .pipe(
                switchMap((billingInformationUpdated: boolean) => {
                    if (billingInformationUpdated) {
                        return this.getOrderDetails().pipe(
                            tap((response: OrderDetailsResponse) => {
                                this.loadDisplayData(response);
                            }),
                        );
                    }

                    return of(void 0);
                }),
            )
            .subscribe();
    }

    /**
     * Handles the click event for adding a new order line item.
     *
     * If the form is submitted and valid, the dialog is closed with the form value. The new order line item is then added to the order line items form array,
     * the main form is marked as dirty, and form listeners are set up for the new item to update the grid row data accordingly.
     *
     * If the form is not saved (i.e., the dialog is closed without submitting), no changes are made.
     */
    handleAddOrderLineItemClick(): void {
        if (hasOpenDialogs(this.dialog)) {
            return;
        }

        this.dialog
            .open(OrderLineItemFormModalComponent, {
                width: '700px',
                disableClose: true,
                data: {},
            })
            .afterClosed()
            .pipe(
                tap(
                    (
                        orderLineItemFormModel:
                            OrderLineItemFormModel | undefined,
                    ) => {
                        if (orderLineItemFormModel) {
                            // Creating a new order line item form and patching the values from the dialog
                            const orderLineItemForm =
                                this.orderLineItemFormService.getFormDefinition();
                            orderLineItemForm.patchValue({
                                ...orderLineItemFormModel,
                                weight: 0, // New items should appear at the top of the list
                                tempId: GridHelper.generateTempRowId(),
                            });

                            // Incrementing the weight of all existing order line items to ensure the new item appears at the top of the list
                            this.orderLineItemFormService.incrementWeightForAllOrderLineItems(
                                this.orderLineItems,
                            );

                            // Adding the new order line item form to the form array
                            this.orderLineItemFormService.addOrderLineItemForm(
                                this.orderLineItems,
                                orderLineItemForm,
                            );

                            // Marking the main form as dirty since a new item has been added
                            this.orderDetailsForm.markAsDirty();

                            // Setting up form listeners for the new order line item to update grid row data accordingly
                            this.setupOrderLineItemFormListeners(
                                orderLineItemForm,
                            );

                            // Updating the form permissions based on the new line item addition to the form
                            this.updateOrderDetailsFormPermissions();

                            // Updating the grid row data to reflect the new order line item
                            this.updateOrderLineItemGridRowData();
                            this.updateOrderFinancialSummaryGridRowData();
                        }
                    },
                ),
            )
            .subscribe();
    }

    isApprovedForInvoiceNet30(): boolean {
        return this.orderDetails.isCreditApproved;
    }

    isAuthNetAim(paymentMethod: PaymentMethod | null): boolean {
        return paymentMethod === PaymentMethod.AuthNetAim;
    }

    isInvoiceNet30(paymentMethod: PaymentMethod | null): boolean {
        return paymentMethod === PaymentMethod.InvoiceNet30;
    }

    getCardSelectionError(): string | undefined {
        const errors =
            this.preferredPaymentMethodForm.controls.cardOnFileId.errors;

        if (!errors) {
            return undefined;
        }

        return errors[
            nameOf<typeof CardOnFileValidationMessages>('creditCardExpired')
        ] as string | undefined;
    }

    editSelectedCreditCard(): void {
        const cardOnFileId =
            this.preferredPaymentMethodForm.controls.cardOnFileId.value;
        if (!cardOnFileId) {
            throw new Error(
                'Card on file ID is not set. Cannot edit a credit card without a card on file ID.',
            );
        }

        const authenticatedUser = this.userStateService.getUser();
        if (!authenticatedUser) {
            throw new Error(
                'User is not authenticated. Cannot edit a credit card without an authenticated user.',
            );
        }

        this.creditCardDialogService
            .openEditCardDialog(
                cardOnFileId,
                authenticatedUser.id,
                this.cardsOnFile,
            )
            .pipe(
                takeUntil(this.destroy$),
                switchMap((result: boolean) => {
                    if (result) {
                        // If the card was successfully updated, then we want to reload the credit cards
                        // to get the updated list (e.g., if the default card was changed, we want that reflected in the dropdown options).
                        return this.loadCardsOnFile().pipe(
                            tap(() => this.setupCardsOnFileDropdownOptions()),
                        );
                    }
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    addNewCreditCard(): void {
        const authenticatedUser = this.userStateService.getUser();
        if (!authenticatedUser) {
            throw new Error(
                'User is not authenticated. Cannot add a new credit card without an authenticated user.',
            );
        }

        if (!this.orderId) {
            throw new Error(
                'Order ID is not set. Cannot add a new credit card without an order ID.',
            );
        }

        this.creditCardDialogService
            .openCreateCardDialog(
                authenticatedUser.id,
                Number(this.orderId),
                this.cardsOnFile,
            )
            .pipe(
                takeUntil(this.destroy$),
                switchMap((creditCardSummary: CreditCardSummary | null) => {
                    if (creditCardSummary != null) {
                        return this.loadCardsOnFileAndApplyPreSelection(
                            creditCardSummary.id,
                        );
                    }

                    return EMPTY;
                }),
            )
            .subscribe();
    }

    /**
     * Resets the preferred payment method form to its initial state by clearing the form values and resetting the
     * form instance, as well as re-setting up the form listeners to ensure they are properly subscribed to
     * the new form instance after the reset.
     */
    resetPreferredPaymentMethodForm(): void {
        this.clearPreferredPaymentMethodForm();

        // After clearing the preferred payment method form, we need to re-setup the form listeners to ensure that they are subscribed to the
        // new form instance.
        this.setupPreferredPaymentMethodFormListeners();
    }

    savePreferredPaymentMethodForm(): void {
        if (this.preferredPaymentMethodForm.valid) {
            this.savePreferredPaymentMethodFormDataToApi().subscribe();
        }
    }

    /**
     * Handles displaying an informational dialog letting the user know that they must have made a selection for their preferred payment method
     * on this order. This will only appear to users with the customer role as they are the only ones who can make a preferred payment method selection.
     */
    private showPreferredPaymentMethodInfoDialog(status: OrderStatus): void {
        const authenticatedUser = this.userStateService.getUser();
        if (!authenticatedUser) {
            // User state may not be hydrated yet; don't crash page initialization.
            return;
        }

        if (authenticatedUser.role !== UserRole.Customer) {
            // If the user is not a customer, then we don't need to show the dialog since only customers can make
            // a preferred payment method selection
            return;
        }

        if (status !== OrderStatus.Pending) {
            // If the order is not in a pending status, then we don't need to show the dialog since the user can no longer make
            // a preferred payment method selection for this order
            return;
        }

        if (this.isPreferredPaymentMethodRequirementSatisfied) {
            // If the preferred payment method is already set, then we don't need to show the dialog since the user has already made
            // a selection for their preferred payment method
            return;
        }

        this.dialog.open(ModalComponent, {
            disableClose: true,
            data: {
                title: PaymentMethodRequiredMessages.title,
                content: PaymentMethodRequiredMessages.content,
                primaryAction: {
                    title: PaymentMethodRequiredMessages.primaryAction.title,
                    name: PaymentMethodRequiredMessages.primaryAction.name,
                    action: () => {
                        this.dialog.closeAll();
                    },
                },
                isShowCancel: false,
            },
        });
    }

    /**
     * Initializes the grid data for the specified grid type.
     *
     * @param type The type of grid to initialize data for.
     */
    private initializeGridData(type: OrderDetailsGridType): void {
        switch (type) {
            case OrderDetailsGridType.orderLineItemsEditable:
            case OrderDetailsGridType.orderLineItemsReadonly:
                return this.updateOrderLineItemGridRowData();
            case OrderDetailsGridType.orderFinancialSummary:
                return this.updateOrderFinancialSummaryGridRowData();
            case OrderDetailsGridType.orderHistoryLogs:
                return this.updateOrderHistoryLogsGridRowData();
            default: {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _exhaustive: never = type;
                throw new Error(`Unhandled grid type: ${type}`);
            }
        }
    }

    /**
     * Sets up grid alignment between the order line items grid and the order totals grid.
     * This ensures that horizontal scrolling is synchronized between the two grids.
     * Alignment is only set up if both grid APIs are available.
     */
    private setupGridAlignment(): void {
        const orderLineItemGridApi =
            this.gridApis[this.getActiveOrderLineItemGridType()];

        const orderFinancialSummaryGridApi =
            this.gridApis[OrderDetailsGridType.orderFinancialSummary];

        if (orderLineItemGridApi && orderFinancialSummaryGridApi) {
            orderLineItemGridApi.setGridOption('alignedGrids', [
                orderFinancialSummaryGridApi,
            ]);
            orderFinancialSummaryGridApi.setGridOption('alignedGrids', [
                orderLineItemGridApi,
            ]);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getOrderLineItemsEditableGridContext(): any {
        return {
            gridType: OrderDetailsGridType.orderLineItemsEditable,
            formArray: this.orderLineItems,
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getOrderLineItemsReadonlyGridContext(): any {
        return {
            gridType: OrderDetailsGridType.orderLineItemsReadonly,
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getOrderFinancialSummaryGridContext(): any {
        return {
            gridType: OrderDetailsGridType.orderFinancialSummary,
        };
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private getOrderHistoryLogsGridContext(): any {
        return {
            gridType: OrderDetailsGridType.orderHistoryLogs,
        };
    }

    /**
     * Determines the active grid type for order line items based on the user's role.
     *
     * @returns The appropriate OrderDetailsGridType for the order line items grid.
     */
    private getActiveOrderLineItemGridType():
        | OrderDetailsGridType.orderLineItemsEditable
        | OrderDetailsGridType.orderLineItemsReadonly {
        return this.hasElevatedRole()
            ? OrderDetailsGridType.orderLineItemsEditable
            : OrderDetailsGridType.orderLineItemsReadonly;
    }

    /**
     * Updates the grid row data for the order line items grid based on the user's role.
     * Elevated roles see the elevated grid, non-elevated roles see the standard grid.
     * The line item data is ordered by their weight to ensure correct display order.
     */
    private updateOrderLineItemGridRowData(): void {
        const gridType = this.getActiveOrderLineItemGridType();

        if (this.gridApis[gridType]) {
            // Clear existing row data before setting new data
            this.gridApis[gridType].setGridOption('rowData', []);

            // Set the new row data from the form array values
            this.gridApis[gridType].setGridOption(
                'rowData',
                // Sort order line items by weight to ensure correct order in the grid
                this.orderLineItems.controls // Weights will always be defined here as they are set when adding new items to the form array
                    .sort((a, b) => a.value.weight! - b.value.weight!)
                    .map(
                        (
                            orderLineItemForm: FormGroup<
                                FormGroupType<OrderLineItemFormModel>
                            >,
                        ) => orderLineItemForm.value,
                    ),
            );
        }
    }

    /**
     * Updates the grid row data for the order financial summary grid.
     */
    private updateOrderFinancialSummaryGridRowData(): void {
        const orderFinancialSummaryGridApi =
            this.gridApis[OrderDetailsGridType.orderFinancialSummary];
        if (!orderFinancialSummaryGridApi) {
            return;
        }

        const orderLineItemTotal = this.orderLineItems.controls.reduce(
            (
                total: number,
                item: FormGroup<FormGroupType<OrderLineItemFormModel>>,
            ) => {
                const quantity = parseMaskedStringToNumber(
                    item.controls.quantity.value,
                );
                const price = parseMaskedStringToNumber(
                    item.controls.price.value,
                );

                return total + quantity * price;
            },
            0,
        );

        const successfulPaymentTransactions =
            this.orderDetails.successfulPaymentTransactions ?? [];
        const paymentTransactionRowData: OrderFinancialSummaryRowData[] =
            successfulPaymentTransactions.map((transaction) =>
                this.transformToRowData(transaction),
            );

        const successfulPaymentsTotal = successfulPaymentTransactions.reduce(
            (total, transaction) => total + transaction.amount,
            0,
        );

        const orderBalanceDue = orderLineItemTotal - successfulPaymentsTotal;

        const orderTotalRowData: OrderFinancialSummaryRowData = {
            label: OrderDetailsConstants.ORDER_TOTAL_GRID_LABEL,
            amount: this.currencyFormattingService.formatToUSD(
                orderLineItemTotal,
            ),
        };

        const orderBalanceDueRowData: OrderFinancialSummaryRowData = {
            label: OrderDetailsConstants.BALANCE_DUE_GRID_LABEL,
            amount: this.currencyFormattingService.formatToUSD(orderBalanceDue),
        };

        orderFinancialSummaryGridApi.setGridOption('rowData', [
            orderTotalRowData,
            ...paymentTransactionRowData,
            orderBalanceDueRowData,
        ]);
    }

    /**
     * Updates the grid row data for the order history logs grid
     */
    private updateOrderHistoryLogsGridRowData(): void {
        if (this.gridApis[OrderDetailsGridType.orderHistoryLogs]) {
            this.gridApis[OrderDetailsGridType.orderHistoryLogs].setGridOption(
                'rowData',
                this.orderDetails.orderHistoryLogs,
            );
        }
    }

    /**
     * Updates the weight of each order line item based on its
     * current position in the grid after a row drag operation.
     */
    private updateOrderLineItemWeights(): void {
        this.gridApis[OrderDetailsGridType.orderLineItemsEditable]?.forEachNode(
            (node) => {
                const orderLineItemFormControl:
                    | FormGroup<FormGroupType<OrderLineItemFormModel>>
                    | undefined = this.orderLineItems.controls.find(
                    (oli) =>
                        // Matching either by the ID of an existing item or the tempId of a newly added item.
                        // Both situations are possible due to the ability to add new items that have not yet been saved to the API.
                        // If neither match is found, an error is thrown.
                        (oli.value.id && oli.value.id === node.data.id) ||
                        (oli.value.tempId &&
                            oli.value.tempId === node.data.tempId),
                );

                if (!orderLineItemFormControl) {
                    throw new Error(
                        `Order line item form control not found for id: ${node.data.id}`,
                    );
                }

                orderLineItemFormControl.patchValue({ weight: node.rowIndex });
                orderLineItemFormControl.markAsDirty();
            },
        );

        // Ensure that the form's dirty state is updated after changing the weights of the order line items
        this.orderDetailsForm.updateValueAndValidity();
    }

    private setupOrderDetailsDropdownOptions(): void {
        this.resourcesService
            .getPermittedOrderStatusTransitions()
            .pipe(
                tap((permittedOrderStatusTransitions) => {
                    this.permittedOrderStatusTransitions =
                        permittedOrderStatusTransitions;

                    this.orderStatusDropdownOptions =
                        this.orderFormService.getStatusTransitionDropdownOptions(
                            this.permittedOrderStatusTransitions,
                            this.status.value,
                        );
                }),
            )
            .subscribe();
    }

    /**
     * Setting up listeners for changes to quantity and rate fields in each order
     * line item to update the grid row data accordingly.
     */
    private setupOrderDetailsFormListeners(): void {
        const orderLineItemControls = this.orderLineItems.controls;
        orderLineItemControls.forEach(
            (
                orderLineItemForm: FormGroup<
                    FormGroupType<OrderLineItemFormModel>
                >,
            ) => {
                this.setupOrderLineItemFormListeners(orderLineItemForm);
            },
        );

        /**
         * Form control listener for the order status field to update
         * form permissions based on the new status value.
         */
        this.status.valueChanges
            .pipe(
                takeUntil(
                    // Automatically unsubscribe when the component is destroyed or when
                    // the save operation is confirmed
                    merge(this.destroy$, this.saveOrderDetailsConfirmation$),
                ),
                tap(() => {
                    this.updateOrderDetailsFormPermissions();
                }),
            )
            .subscribe();
    }

    /**
     * Sets up form control listeners for an individual order line item form.
     */
    private setupOrderLineItemFormListeners(
        orderLineItemForm: FormGroup<FormGroupType<OrderLineItemFormModel>>,
    ): void {
        // Setting up listeners for quantity and price changes to update grid row data
        this.orderLineItemFormService
            .setupQuantityAndPriceFormControlListeners(
                orderLineItemForm,
                this.gridApis[OrderDetailsGridType.orderLineItemsEditable],
                () => this.updateOrderFinancialSummaryGridRowData(),
                merge(this.destroy$, this.saveOrderDetailsConfirmation$),
            )
            .subscribe();

        // Setting up listener for `IsDeleted` control to manage enabling/disabling
        // the last non-deleted form group's `IsDeleted` control
        this.orderLineItemFormService
            .setupIsDeletedFormControlListener(
                orderLineItemForm,
                this.orderLineItems,
                merge(this.destroy$, this.saveOrderDetailsConfirmation$),
            )
            .subscribe();
    }

    /**
     * Handles enabling/disabling portions of the form based on user role, order status, and order line item
     * deletion states. All operations in this method must execute together to maintain consistent form state.
     */
    private updateOrderDetailsFormPermissions(): void {
        // Step 1: Control form-level access based on user role and order status
        const isElevated = this.hasElevatedRole();
        const isReadOnly = this.isReadOnly();

        if (!isElevated || isReadOnly) {
            this.orderDetailsForm.disable({ emitEvent: false });

            // Exception: Elevated users can change status even in read-only mode
            if (isElevated && isReadOnly) {
                this.orderDetailsForm.controls.status.enable({
                    emitEvent: false,
                });
            }

            // Exception: Non-elevated users can update their PO number if the order is not in read-only mode
            if (!isElevated && !isReadOnly) {
                this.orderDetailsForm.controls.poNumber.enable({
                    emitEvent: false,
                });
            }
        } else {
            this.orderDetailsForm.enable({ emitEvent: false });
        }

        // Step 2 and Step 3 only apply to elevated users since non-elevated users cannot interact with order line items at all.
        // Non-elevated users on pending orders may edit their PO number, but all other fields (including order line items) remain read-only.
        if (this.hasElevatedRole()) {
            // Step 2: At least one order line item must remain unmarked for deletion when the order is not in read-only mode
            if (!this.isReadOnly()) {
                this.orderLineItemFormService.updateIsDeletedControlStates(
                    this.orderLineItems,
                );
            }

            // Step 3: Sync grid UI with access control state (show/hide drag handles)
            this.gridApis[
                OrderDetailsGridType.orderLineItemsEditable
            ]?.setGridOption(
                'columnDefs',
                this.orderLineItemsEditableGridColumnDefs(),
            );
        }
    }

    /**
     * Handles form dirty state tracking.
     * It listens for changes in the form and updates
     * the hasChanges flag accordingly.
     */
    private handleOrderDetailsFormDirtying(): void {
        this.formEntryService.handleFormDirtying<OrderDetailsFormModel>(
            this.orderDetailsForm,
            this.destroy$,
            (isFormDirty: boolean) => {
                this.hasChanges = isFormDirty;
            },
        );
    }

    /**
     * Handles the click event for saving changes to the order details.
     * It will save the form data to the API if the action is confirmed. Confirmation
     * is required if there are unsaved changes and the order is not in a pending status
     * as the API only allows updates to orders in a pending status.
     */
    private handleOrderDetailsSaveChangesClick(): void {
        this.orderDetailsActionsService.saveChanges$
            .pipe(
                takeUntil(this.destroy$),
                switchMap(() => this.saveWithOptionalConfirmation()),
                tap((saveConfirmation) => {
                    if (saveConfirmation) {
                        // Resetting the form's state and the flag for unsaved changes after a successful save operation
                        this.orderDetailsForm.markAsPristine();
                        this.hasChanges = false;

                        // Displaying a success alert to the user after successfully saving changes to the order details
                        this.alertService.displaySuccessAlert(
                            OrderDetailsConstants.SUCCESS_ALERT_MESSAGE,
                            false,
                            { durationInMs: 5000 },
                        );
                    }
                }),
            )
            .subscribe();
    }

    /**
     * Handles the click event for printing billing documents.
     *
     * It will display a warning modal if the order is still pending.
     * If the order is not pending, it will open the billing document PDF in a new window.
     */
    private handleOrderDetailsPrintClick(): void {
        this.orderDetailsActionsService.print$
            .pipe(
                takeUntil(this.destroy$),
                switchMap(() => {
                    if (!this.orderId || this.dialog.openDialogs.length > 0) {
                        return EMPTY;
                    }

                    if (this.status.value === OrderStatus.Pending) {
                        this.dialog.open(ModalComponent, {
                            data: {
                                title: OrderPrintBillingDocumentWarningDialogMessage.modalTitle,
                                content:
                                    OrderPrintBillingDocumentWarningDialogMessage.modalContent,
                            },
                        });
                        return EMPTY;
                    }

                    return openPdfInNewWindow(
                        this.orderService.getBillingDocumentPdf(this.orderId),
                    );
                }),
            )
            .subscribe();
    }

    /**
     * Handles the confirmation or direct save process for the order details.
     * It checks if confirmation is required based on whether there are changes to the
     * order grid or notes that would be lost when saving to a non-pending status.
     * A status-only change does not require confirmation since no other changes would be lost.
     * If confirmation is needed, it opens a modal dialog and proceeds based on the user's choice.
     *
     * @returns An observable that emits a boolean indicating whether the save operation was done or not.
     */
    private saveWithOptionalConfirmation(): Observable<boolean> {
        const hasChangesToFormExcludingStatus =
            this.orderLineItems.dirty ||
            this.notes.dirty ||
            this.poNumber.dirty;
        const requiresConfirmation =
            hasChangesToFormExcludingStatus &&
            this.status.value !== OrderStatus.Pending;

        const confirmed$ = requiresConfirmation
            ? this.dialog
                  .open(ModalComponent, {
                      disableClose: true,
                      data: {
                          title: OrderSaveConfirmationDialogMessages.modalTitle,
                          content:
                              OrderSaveConfirmationDialogMessages.modalContent,
                          primaryAction: {
                              title: OrderSaveConfirmationDialogMessages.modalPrimaryActionTitle,
                          },
                      },
                  })
                  .afterClosed()
            : of(true);

        return confirmed$.pipe(
            switchMap((confirmed: boolean) =>
                confirmed
                    ? this.saveOrderDetailsFormDataToApi().pipe(map(() => true))
                    : of(false),
            ),
        );
    }

    /**
     * Generates the request object for saving the order details form data to the API.
     *
     * @returns The SaveOrderRequest object containing the form data.
     */
    private generateOrderDetailsRequestObject(): SaveOrderRequest {
        const formValue = this.orderDetailsForm.value as OrderDetailsFormModel;

        return {
            status: formValue.status,
            notes: formValue.notes,
            poNumber: formValue.poNumber,
            orderLineItems: this.orderLineItemFormService.generateRequestObject(
                this.orderLineItems,
            ),
        };
    }

    /**
     * Saves the current form data to the API and refreshes
     * the order details form data upon successful save.
     *
     * @returns An observable that completes when the form data is saved and refreshed.
     */
    private saveOrderDetailsFormDataToApi(): Observable<void> {
        return this.orderService
            .save(this.orderId!, this.generateOrderDetailsRequestObject())
            .pipe(
                switchMap(() => this.getOrderDetails()),
                tap(() => {
                    // This is crucial in ensuring that the form listeners unsubscribe before the form is reset
                    // to prevent memory leaks and unintended side effects from lingering subscriptions
                    // after the form data is refreshed with the latest data from the API.
                    this.saveOrderDetailsConfirmation$.next();
                }),
                tap(() => {
                    // Resetting the form to allow for it to be reloaded with the updated data from the API.
                    this.orderDetailsFormService.resetForm(
                        this.orderDetailsForm,
                    );
                }),
                tap((response: OrderDetailsResponse) => {
                    this.loadOrderDetailsFormData(response);
                }),
                tap((response: OrderDetailsResponse) => {
                    this.loadDisplayData(response);
                }),
                tap((response: OrderDetailsResponse) => {
                    this.togglePreferredPaymentMethodForm(response);
                }),
                tap(() => {
                    // Form listeners need to be re-established after the original listeners unsubscribed when the save operation was triggered
                    this.setupOrderDetailsFormListeners();
                }),
                tap(() => {
                    // After saving, the dropdown options must be re-evaluated in the case that the status was changed
                    // This ensures that the correct options are enabled/disabled based on the new status of the order
                    this.orderStatusDropdownOptions =
                        this.orderFormService.getStatusTransitionDropdownOptions(
                            this.permittedOrderStatusTransitions,
                            this.status.value,
                        );
                }),
                map(() => void 0),
            );
    }

    /**
     * Creates an observable that fetches order details from the API.
     */
    private getOrderDetails(): Observable<OrderDetailsResponse> {
        if (!this.orderId) {
            throw new Error('Order ID is not set');
        }

        return this.orderService.get(this.orderId);
    }

    /**
     * Loads the order details based on the order ID from the route parameters.
     * It fetches the order details from the API, transforms the response data,
     * and binds the data to the form and page display.
     *
     * @returns An observable that completes when the order details are loaded and bound.
     */
    private loadOrderDetails(): Observable<void> {
        return this.route.paramMap.pipe(
            takeUntil(this.destroy$),
            switchMap((params: ParamMap) => {
                this.orderId = params.get('id')!;

                return this.getOrderDetails();
            }),
            tap((response: OrderDetailsResponse) => {
                const { status, preferredPaymentMethod } = response;

                this.isPreferredPaymentMethodRequirementSatisfied =
                    status !== OrderStatus.Pending ||
                    preferredPaymentMethod != null;
            }),
            tap((response: OrderDetailsResponse) => {
                this.showPreferredPaymentMethodInfoDialog(response.status);
            }),
            tap((response: OrderDetailsResponse) => {
                this.loadDisplayData(response);
            }),
            tap((response: OrderDetailsResponse) => {
                this.loadOrderDetailsFormData(response);
            }),
            tap((response: OrderDetailsResponse) => {
                this.togglePreferredPaymentMethodForm(response);
            }),
            switchMap((response: OrderDetailsResponse) =>
                this.loadPreferredPaymentMethodFormData(response),
            ),
        );
    }

    /**
     * Loads the data for the page display (non-form data) from the API response.
     */
    private loadDisplayData(response: OrderDetailsResponse): void {
        this.orderDetails = this.transformResponseToData(response);
        this.orderDetailsActionsService.setPrintButtonLabel(
            this.orderDetails.balance,
        );

        // Load the grid row data for the order history logs grid
        // after transforming the response data for the page display
        this.updateOrderHistoryLogsGridRowData();
    }

    /**
     * Loads the data for the form from the API response and binds it to the form controls.
     */
    private loadOrderDetailsFormData(response: OrderDetailsResponse): void {
        this.bindOrderDetailsForm(response);

        // Ensuring access control is updated after binding the form
        this.updateOrderDetailsFormPermissions();

        // Update the grid row data after binding the form for order line items
        this.updateOrderLineItemGridRowData();
        this.updateOrderFinancialSummaryGridRowData();
    }

    private bindOrderDetailsForm(response: OrderDetailsResponse): void {
        // Patching the form value with the response data. This
        // does not include the order line items, which are patched
        // separately below.
        this.orderDetailsForm.patchValue(response);

        // Patching the order line items from the response to the
        // order line items form array
        this.orderLineItemFormService.patchFormArrayValue(
            this.orderLineItems,
            response.orderLineItems,
        );
    }

    private transformResponseToData(
        response: OrderDetailsResponse,
    ): OrderDetailsData {
        const { billingInformation, ...order } = response;

        return {
            id: order.id,
            date: order.billingDate
                ? parseDateToEasternGridFormat(order.billingDate)
                : parseDateToEasternGridFormat(order.createdAt),
            customerEmail: order.customerEmail,
            coordinatorName: order.coordinatorName,
            coordinatorEmail: order.coordinatorEmail,
            applicantName: order.licenseeEntityName,
            transmissionLocation: [
                order.transmissionLocationCity,
                order.transmissionLocationState,
            ]
                .filter(Boolean)
                .join(', '),
            salmonLogId: order.salmonLogId,
            pscaId: order.coordinationId,
            callSign: order.callSign || 'New, tba',
            balance: order.balance,
            isCreditApproved: order.isCreditApproved,
            orderBillingInformationId: billingInformation.id,
            organizationName: billingInformation.organizationName,
            contactName: billingInformation.contactName,
            contactEmail: billingInformation.contactEmail,
            primaryStreetAddress: billingInformation.primaryStreetAddress,
            secondaryStreetAddress: billingInformation.secondaryStreetAddress,
            city: billingInformation.city,
            state: billingInformation.state,
            zipCode: billingInformation.zipCode,
            poNumber: order.poNumber,
            orderHistoryLogs: order.orderHistoryLogs.map((log) => ({
                createdAt: log.createdAt,
                status: OrderStatusLabels[log.status],
                username: log.username,
                message: log.message,
            })),
            successfulPaymentTransactions:
                order.successfulPaymentTransactions.map((transaction) => {
                    return {
                        id: transaction.id,
                        paymentMethod: transaction.paymentMethod,
                        status: transaction.status,
                        amount: transaction.amount,
                        cardType: transaction.cardType,
                        truncatedCardNumber: transaction.truncatedCardNumber,
                        checkNumber: transaction.checkNumber,
                        confirmedAt: transaction.confirmedAt,
                        createdAt: transaction.createdAt,
                    };
                }),
        };
    }

    /**
     * Transforms an order details payment transaction response into a row data object suitable for display in the order total section.
     *
     * @param response The order details payment transaction response to transform.
     * @returns The transformed order total row data object.
     */
    private transformToRowData(
        response: OrderDetailsPaymentTransactionResponse,
    ): OrderFinancialSummaryRowData {
        let label: string;

        if (response.paymentMethod === PaymentMethod.AuthNetAim) {
            const action =
                response.status === OrderPaymentTransactionStatus.Refunded
                    ? 'Refunded to'
                    : 'Paid with';
            const cardType = response.cardType
                ? titleize(response.cardType)
                : 'card';
            const cardSuffix = response.truncatedCardNumber
                ? ` ending in XXXX${response.truncatedCardNumber}`
                : '';

            label = `${action} ${cardType}${cardSuffix} on ${parseDateToEasternGridFormat(response.createdAt)}`;
        } else if (
            InvoiceNet30TransitionablePaymentMethods.includes(
                response.paymentMethod,
            )
        ) {
            const paymentReferencePrefixByMethod: Partial<
                Record<PaymentMethod, string>
            > = {
                [PaymentMethod.Check]: 'Check # ',
                [PaymentMethod.CreditCard]: 'Credit Card ',
                [PaymentMethod.ElectronicFundsTransfer]: 'EFT ',
                [PaymentMethod.Drawdown]: 'Drawdown Account ',
            };
            const paymentReferencePrefix =
                paymentReferencePrefixByMethod[response.paymentMethod] ?? '';
            const paymentReferenceSuffix = response.checkNumber
                ? ` with ${paymentReferencePrefix}${response.checkNumber}`
                : '';

            label = response.confirmedAt
                ? `Paid on ${parseDateToEasternGridFormat(response.confirmedAt)}${paymentReferenceSuffix}`
                : `Paid${paymentReferenceSuffix}`;
        } else {
            label = `Paid on ${parseDateToEasternGridFormat(response.createdAt)}`;
        }

        return {
            label,
            amount: this.currencyFormattingService.formatToUSD(response.amount),
        };
    }

    /**
     * Calculates the total for a given order line item.
     *
     * @param orderLineItem The order line item to calculate the total for.
     * @returns The formatted total as a string in USD.
     */
    private calculateOrderLineItemTotal(
        orderLineItem: OrderLineItemFormModel,
    ): string {
        const quantity = parseMaskedStringToNumber(orderLineItem.quantity);
        const price = parseMaskedStringToNumber(orderLineItem.price);
        const total = quantity * price;

        return this.currencyFormattingService.formatToUSD(total);
    }

    private setupPreferredPaymentMethodFormListeners(): void {
        this.preferredPaymentMethodFormService.setupFormListeners(
            this.preferredPaymentMethodForm,
            () => this.cardsOnFile,
            merge(this.destroy$, this.savePreferredPaymentMethodConfirmation$),
        );
    }

    private setupCardsOnFileDropdownOptions(): void {
        const orderedCreditCards =
            this.creditCardSummaryService.orderCreditCards(this.cardsOnFile);

        this.cardsOnFileDropdownOptions =
            this.dropdownService.transformEligibleCardsForOrderToDropdownOptions(
                orderedCreditCards,
                this.creditCardSummaryService.isExpired,
            );
    }

    private generatePreferredPaymentMethodRequestObject(): SetPreferredPaymentMethodRequest {
        const formValue = this.preferredPaymentMethodForm
            .value as PreferredPaymentMethodFormModel;

        return {
            paymentMethod: formValue.paymentMethod,
            cardOnFileId: formValue.cardOnFileId,
        };
    }

    private savePreferredPaymentMethodFormDataToApi(): Observable<void> {
        if (!this.orderId) {
            throw new Error('Order ID is not set');
        }

        return this.orderService
            .setPreferredPaymentMethod(
                this.orderId,
                this.generatePreferredPaymentMethodRequestObject(),
            )
            .pipe(
                tap(() => {
                    this.alertService.displaySuccessAlert(
                        PreferredPaymentMethodFormMessages.SUCCESS_ALERT_MESSAGE,
                        false,
                        { durationInMs: 5000 },
                    );
                }),
                tap(() => {
                    this.clearPreferredPaymentMethodForm();
                }),
                switchMap(() => this.getOrderDetails()),
                tap((response: OrderDetailsResponse) => {
                    const { status, preferredPaymentMethod } = response;

                    this.isPreferredPaymentMethodRequirementSatisfied =
                        status !== OrderStatus.Pending ||
                        preferredPaymentMethod != null;
                }),
                switchMap((response: OrderDetailsResponse) =>
                    this.loadPreferredPaymentMethodFormData(response),
                ),
                tap(() => {
                    this.setupPreferredPaymentMethodFormListeners();
                }),
            );
    }

    /**
     * Clears the preferred payment method form by resetting it to a new instance of the form definition and unsubscribing from any existing
     * form listeners to prevent memory leaks.
     */
    private clearPreferredPaymentMethodForm(): void {
        // This is crucial in ensuring that the form listeners unsubscribe before the form is reset
        // to prevent memory leaks and unintended side effects from lingering subscriptions
        // after the form data is refreshed with the latest data from the API.
        this.savePreferredPaymentMethodConfirmation$.next();

        // Getting a new form instance to reset the form state entirely.
        this.preferredPaymentMethodForm =
            this.preferredPaymentMethodFormService.getFormDefinition();

        // Refreshes and applies the appropriate validators for the payment method after resetting the form.
        this.preferredPaymentMethodFormService.refreshAndApplyPaymentMethodValidators(
            this.preferredPaymentMethodForm,
            () => this.cardsOnFile,
        );
    }

    /**
     * Enables or disables the preferred payment method form based on the order status.
     *
     * @param response The order details response from the API.
     */
    private togglePreferredPaymentMethodForm(
        response: OrderDetailsResponse,
    ): void {
        if (response.status !== OrderStatus.Pending) {
            this.preferredPaymentMethodForm.disable({ emitEvent: false });
        } else {
            this.preferredPaymentMethodForm.enable({ emitEvent: false });
        }
    }

    private loadPreferredPaymentMethodFormData(
        response: OrderDetailsResponse,
    ): Observable<void> {
        return this.loadCardsOnFileAndApplyPreSelection().pipe(
            tap(() => {
                this.patchPreferredPaymentMethodFromResponse(response);
            }),
        );
    }

    /**
     * Patches the preferred payment method form with the data from the API response and refreshes the form
     * validators accordingly.
     */
    private patchPreferredPaymentMethodFromResponse(
        response: OrderDetailsResponse,
    ): void {
        this.preferredPaymentMethodForm.patchValue({
            paymentMethod: response.preferredPaymentMethod,
        });

        this.preferredPaymentMethodFormService.refreshAndApplyPaymentMethodValidators(
            this.preferredPaymentMethodForm,
            () => this.cardsOnFile,
        );
    }

    /**
     * Loads the user's saved credit cards on file from the API and applies pre-selection logic to the preferred payment method form.
     * If a pre-selected card on file ID is provided (e.g., after adding a new card), it patches the form with that value to ensure
     * the user's selection is retained.
     *
     * @param preSelectedCardOnFileId An optional card on file ID to pre-select in the form after loading the cards on file.
     */
    private loadCardsOnFileAndApplyPreSelection(
        preSelectedCardOnFileId: number | null = null,
    ): Observable<void> {
        return this.loadCardsOnFile().pipe(
            tap(() => {
                this.preferredPaymentMethodFormService.selectPreferredOrDefaultCard(
                    this.preferredPaymentMethodForm,
                    () => this.cardsOnFile,
                    preSelectedCardOnFileId,
                );
            }),
            tap(() => {
                this.setupCardsOnFileDropdownOptions();
            }),
        );
    }

    private loadCardsOnFile(): Observable<void> {
        const authenticatedUser = this.userStateService.getUser();
        if (!authenticatedUser) {
            throw new Error('User is not authenticated');
        }

        const orderId = Number(this.orderId);
        if (!orderId) {
            throw new Error('Order ID is not set');
        }

        return this.creditCardService
            .getAllForOrder(authenticatedUser.id, orderId)
            .pipe(
                takeUntil(this.destroy$),
                tap((response: GetEligibleCardsForOrderResponse) => {
                    // Storing the user's saved credit cards to be used for populating the cards on file
                    // dropdown and for form validation (e.g., checking if the selected card is expired).
                    this.cardsOnFile = response.creditCards;
                }),
                map(() => void 0),
            );
    }

    private orderLineItemsEditableGridColumnDefs(): ColDef[] {
        return [
            GridHelper.dragRowColDef(!this.isReadOnly()),
            GridHelper.deleteRowColDef,
            {
                headerName: 'Service',
                field: nameOf<OrderLineItemFormModel>('productName'),
                filter: false,
                sortable: false,
                minWidth: 600,
                flex: 2,
                headerClass: 'ag-header-left',
                cellStyle: { textAlign: 'left' },
            },
            {
                headerName: 'Description',
                headerClass: 'ag-header-left',
                field: nameOf<OrderLineItemFormModel>('description'),
                filter: false,
                sortable: false,
                minWidth: 600,
                maxWidth: 600,
                ...GridHelper.defaultFormInputCellRendererColDef,
                cellRendererParams: {
                    title: 'Description',
                    classList: 'text-left',
                    required: false,
                    type: 'text',
                    multiline: true,
                    autoGrowMinRows: 1,
                    maxLength: OrderLineItemConstants.DESCRIPTION_MAX_LENGTH,
                    pattern:
                        RegExPattern.PrintableAsciiAndPermittedControlCharactersExceptAngleBracketsAndPipes,
                } as FormInputCellRendererParams,
            },
            {
                headerName: 'Quantity',
                field: nameOf<OrderLineItemFormModel>('quantity'),
                filter: false,
                sortable: false,
                minWidth: 120,
                maxWidth: 150,
                ...GridHelper.defaultFormInputCellRendererColDef,
                cellRendererParams: {
                    title: 'Quantity',
                    classList: 'text-center',
                    imask: OrderLineItemMaskConfig.quantity,
                } as FormInputCellRendererParams,
            },
            {
                headerName: 'Rate',
                field: nameOf<OrderLineItemFormModel>('price'),
                filter: false,
                sortable: false,
                minWidth: 120,
                maxWidth: 150,
                ...GridHelper.defaultFormInputCellRendererColDef,
                headerClass: 'ag-header-right',
                cellRendererParams: {
                    title: 'Rate',
                    classList: 'text-right',
                    imask: OrderLineItemMaskConfig.rate,
                } as FormInputCellRendererParams,
            },
            {
                headerName: 'Total',
                valueGetter: (
                    params: ValueGetterParams<OrderLineItemFormModel>,
                ) => {
                    if (!params.data) {
                        return this.currencyFormattingService.formatToUSD(0);
                    }

                    return this.calculateOrderLineItemTotal(params.data);
                },
                filter: false,
                sortable: false,
                minWidth: 120,
                maxWidth: 150,
                flex: 1,
                headerClass: 'ag-header-right',
                cellStyle: { textAlign: 'right' },
            },
        ];
    }

    private orderLineItemsReadonlyGridColumnDefs(): ColDef[] {
        return [
            {
                headerName: 'Service',
                field: nameOf<OrderLineItemFormModel>('productName'),
                filter: false,
                sortable: false,
                flex: 2,
                minWidth: 600,
                headerClass: 'ag-header-left',
                cellStyle: { textAlign: 'left' },
            },
            {
                headerName: 'Description',
                field: nameOf<OrderLineItemFormModel>('description'),
                filter: false,
                sortable: false,
                minWidth: 600,
                maxWidth: 600,
                headerClass: 'ag-header-left',
                cellStyle: { textAlign: 'left' },
            },
            {
                headerName: 'Quantity',
                field: nameOf<OrderLineItemFormModel>('quantity'),
                valueFormatter: (
                    params: ValueFormatterParams<OrderLineItemFormModel>,
                ) => {
                    return IMask.pipe(
                        params.data?.quantity ?? '',
                        OrderLineItemMaskConfig.quantity,
                    );
                },
                filter: false,
                sortable: false,
                minWidth: 120,
                maxWidth: 150,
            },
            {
                headerName: 'Rate',
                field: nameOf<OrderLineItemFormModel>('price'),
                valueFormatter: (
                    params: ValueFormatterParams<OrderLineItemFormModel>,
                ) => {
                    return this.currencyFormattingService.formatToUSD(
                        parseMaskedStringToNumber(params.data?.price),
                    );
                },
                filter: false,
                sortable: false,
                minWidth: 120,
                maxWidth: 150,
                headerClass: 'ag-header-right',
                cellStyle: { textAlign: 'right' },
            },
            {
                headerName: 'Total',
                valueGetter: (
                    params: ValueGetterParams<OrderLineItemFormModel>,
                ) => {
                    if (!params.data) {
                        return this.currencyFormattingService.formatToUSD(0);
                    }

                    return this.calculateOrderLineItemTotal(params.data);
                },
                filter: false,
                sortable: false,
                minWidth: 120,
                maxWidth: 150,
                flex: 1,
                headerClass: 'ag-header-right',
                cellStyle: { textAlign: 'right' },
            },
        ];
    }

    private orderFinancialSummaryGridColumnDefs(): ColDef[] {
        return [
            {
                field: nameOf<OrderFinancialSummaryRowData>('label'),
                filter: false,
                sortable: false,
                flex: 2,
                minWidth: 300,
                cellStyle: { textAlign: 'right' },
                ...GridHelper.disableCellFocusBorderColDef,
            },
            {
                field: nameOf<OrderFinancialSummaryRowData>('amount'),
                headerName: '',
                filter: false,
                sortable: false,
                flex: 1,
                minWidth: 120,
                maxWidth: 150,
                cellStyle: { textAlign: 'right' },
                ...GridHelper.disableCellFocusBorderColDef,
            },
        ];
    }

    private orderHistoryLogsGridColumnDefs(): ColDef[] {
        return [
            {
                headerName: 'Created On',
                field: nameOf<OrderHistoryLogRowData>('createdAt'),
                valueFormatter: (
                    params: ValueFormatterParams<OrderHistoryLogRowData>,
                ) => {
                    if (!params.data) {
                        return '';
                    }

                    return parseDateToEasternGridFormat(params.data.createdAt);
                },
                filter: false,
                sortable: false,
                sort: 'desc',
                flex: 1,
                minWidth: 150,
                maxWidth: 200,
            },
            {
                headerName: 'Username',
                field: nameOf<OrderHistoryLogRowData>('username'),
                filter: false,
                sortable: false,
                flex: 1,
                minWidth: 150,
                maxWidth: 250,
            },
            {
                headerName: 'Order Status',
                field: nameOf<OrderHistoryLogRowData>('status'),
                filter: false,
                sortable: false,
                flex: 1,
                minWidth: 150,
                maxWidth: 250,
            },
            {
                headerName: 'Message',
                field: nameOf<OrderHistoryLogRowData>('message'),
                filter: false,
                sortable: false,
                flex: 2,
                minWidth: 250,
            },
        ];
    }
}
